<template>
  <!-- Skip link for keyboard users -->
  <a href="#main" class="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:bg-white focus:text-black focus:px-3 focus:py-2 focus:rounded">
    跳到主要内容
  </a>
  <nav class="bg-gray-100 border-gray-200 dark:bg-gray-900" ref="navRef" aria-label="Main">
    <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-3">
      <!-- Logo & Site Title -->
<NuxtLink :to="localePath('/')" class="flex items-center" aria-label="返回首页">
        <nuxt-img
          format="webp"
          src="/HYS-logo-icon-06.png"
          class="sm:h-12 h-8 mr-2"
          alt="HYS Logo"
        />
        <span
          class="self-center sm:text-3xl text-2xl font-semibold text-primaryColor
                 hover:text-secondaryColor whitespace-nowrap dark:text-white"
        >
          {{ $t("global.site_title") }}
        </span>
      </NuxtLink>

      <!-- Right side items (hamburger button, etc.) -->
      <div class="flex items-center md:order-2">
        <!-- Language Switcher (Desktop) -->
        <div class="hidden md:block relative mr-4">
          <button
            ref="langButtonRef"
            @click="toggleLanguageDropdown"
            type="button"
            class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 
                   bg-white border border-gray-300 rounded-lg hover:bg-gray-50 
                   focus:outline-none focus:ring-2 focus:ring-primaryColor"
            aria-haspopup="listbox"
            :aria-expanded="isLanguageDropdownOpen.toString()"
          >
            {{ isChineseLocale ? 'Language' : '语言' }}
            <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <!-- Language Dropdown -->
          <div
            v-if="isLanguageDropdownOpen"
            class="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md 
                   bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
            role="listbox"
            @keydown.esc.prevent.stop="isLanguageDropdownOpen = false"
          >
            <button
              v-for="locale in availableLocales"
              :key="locale.code"
              type="button"
              @click="switchLanguage(locale.code)"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
              role="option"
            >
              {{ locale.name }}
            </button>
          </div>
        </div>

        <!-- Combined Mobile Menu Button -->
        <button
          ref="hamburgerRef"
          @click="toggleMenu"
          type="button"
          class="md:hidden inline-flex items-center gap-1 px-2 py-2 text-sm text-gray-500 
                 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2
                 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700
                 dark:focus:ring-gray-600"
          :aria-expanded="isMenuOpen.toString()"
          aria-controls="main-menu"
        >
          <!-- Language Icon -->
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path 
              stroke-linecap="round" 
              stroke-linejoin="round" 
              stroke-width="2" 
              d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
            />
          </svg>
          <!-- Divider -->
          <div class="w-px h-5 bg-gray-300 dark:bg-gray-600"></div>
          <!-- Hamburger Icon -->
          <svg
            class="w-5 h-5"
            fill="currentColor"
            aria-hidden="true"
            viewBox="0 0 20 20"
          >
            <path
              fill-rule="evenodd"
              d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="sr-only">Open main menu</span>
        </button>
      </div>

      <!-- Collapsible Menu (Mobile) -->
      <div
        ref="menuRef"
        class="w-full md:w-auto md:order-1"
        :class="[ isMenuOpen ? 'block' : 'hidden', 'md:flex' ]"
        id="main-menu"
        @keydown.esc.prevent.stop="closeMenu()"
      >
        <ul
          class="flex flex-col p-4 mt-4 border border-gray-100 rounded-lg
                 md:flex-row md:space-x-8 md:mt-0 md:border-0 md:items-center
                 dark:bg-gray-900 md:dark:bg-transparent"
        >
          <!-- Nav Item: Home -->
          <li>
            <NuxtLink
              :to="localePath('/')"
              @click="closeMenu"
              class="block py-2 pl-3 pr-4 rounded"
              :class="route.path === localePath('/')
                ? activeClass
                : defaultClass"
              aria-current="page"
            >
              {{ $t("header.home") }}
            </NuxtLink>
          </li>

          <!-- Nav Item: Find a Doctor -->
          <li>
            <NuxtLink
              :to="localePath('/doctor')"
              @click="closeMenu"
              class="block py-2 pl-3 pr-4 rounded"
              :class="route.path === localePath('/doctor')
                ? activeClass
                : defaultClass"
            >
              {{ $t("header.find_doctor") }}
            </NuxtLink>
          </li>

          <!-- Nav Item: Find a Specialist -->
          <li>
            <NuxtLink
              :to="localePath('/specialty')"
              @click="closeMenu"
              class="block py-2 pl-3 pr-4 rounded"
              :class="route.path === localePath('/specialty')
                ? activeClass
                : defaultClass"
            >
              {{ $t("header.find_specialist") }}
            </NuxtLink>
          </li>

          <!-- Nav Item: Provider Service -->
          <li>
            <NuxtLink
              :to="localePath('/provider/4/login')"
              @click="closeMenu"
              class="block py-2 pl-3 pr-4 rounded"
              :class="route.path.startsWith(localePath('/provider'))
                ? activeClass
                : defaultClass"
            >
              {{ $t("header.provider_service") }}
            </NuxtLink>
          </li>

          <!-- Nav Item: Blogs -->
          <li v-show="false">
            <NuxtLink
              :to="localePath('/blogs')"
              @click="closeMenu"
              class="block py-2 pl-3 pr-4 rounded"
              :class="route.path === localePath('/blogs')
                ? activeClass
                : defaultClass"
            >
              {{ $t("header.blogs") }}
            </NuxtLink>
          </li>
        </ul>
        
        <!-- Language Switcher (Mobile) -->
        <div class="md:hidden border-t border-gray-200 mt-4 pt-4">
          <p class="px-4 py-2 text-sm text-gray-500">{{ $t("header.select_language") }}</p>
          <div class="space-y-2">
            <button
              v-for="locale in availableLocales"
              :key="locale.code"
              type="button"
              @click="switchLanguage(locale.code)"
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
            >
              {{ locale.name }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useFlowbite } from '~/composables/useFlowbite'
import { useLocalePath, useI18n } from '#i18n'

const route = useRoute()
const localePath = useLocalePath()

// CSS classes for active vs. default states
const activeClass = `
  text-white bg-primaryBtn md:text-lg md:bg-transparent
  md:text-secondaryText md:p-0 md:dark:text-primary-500
`
const defaultClass = `
  md:text-lg text-gray-700 rounded hover:bg-gray-100
  md:hover:bg-transparent md:hover:text-primaryColor md:p-0
  dark:text-white md:dark:hover:text-primary-500
  dark:hover:bg-gray-700 dark:hover:text-white
  md:dark:hover:bg-transparent dark:border-gray-700
`

// Controls mobile menu visibility
const isMenuOpen = ref(false)
const isLanguageDropdownOpen = ref(false)

// Refs for DOM elements
const navRef = ref(null)
const menuRef = ref(null)
const hamburgerRef = ref(null)
const langButtonRef = ref(null)

// Language switcher state and logic
const { locale, setLocale } = useI18n()

const localeDisplayNames = {
  'en-US': 'English',
  'zh-Hans': '简体中文',
  'zh-Hant': '繁體中文',
  'zh': '简体中文'
}

const currentLocaleDisplay = computed(() => localeDisplayNames[locale.value])
const isChineseLocale = computed(() => locale.value.startsWith('zh'))

const availableLocales = computed(() => {
  const allLocales = [
    { code: 'en-US', name: 'English' },
    { code: 'zh-Hans', name: '简体中文' },
    { code: 'zh-Hant', name: '繁體中文' }
  ]
  return allLocales.filter(l => l.code !== locale.value)
})

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

function toggleLanguageDropdown() {
  isLanguageDropdownOpen.value = !isLanguageDropdownOpen.value
}

function toggleMobileLanguageMenu() {
  isMenuOpen.value = true // Open the mobile menu
}

function closeMenu() {
  isMenuOpen.value = false
}

async function switchLanguage(newLocale) {
  await setLocale(newLocale)
  isLanguageDropdownOpen.value = false
  closeMenu()
}

function handleClickOutside(e) {
  // Early return if both menus are closed
  if (!isMenuOpen.value && !isLanguageDropdownOpen.value) return

  // Check if click is inside any of the interactive elements
  const isClickInsideMenu = hamburgerRef.value?.contains(e.target) || 
                           menuRef.value?.contains(e.target) ||
                           langButtonRef.value?.contains(e.target)
  
  // Close menus if click is outside
  if (!isClickInsideMenu) {
    closeMenu()
    isLanguageDropdownOpen.value = false
  }
}

onMounted(() => {
  useFlowbite(() => {
    initFlowbite()
  })
  window.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
})
</script>
